import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { AlertCircle, TrendingUp, DollarSign, PieChart } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const API_URL = window.location.origin;

/**
 * Product Mix Panel
 * 
 * Displays product type breakdown within a vertical with key metrics:
 * - Issued count
 * - Commission sum
 * - Revenue per lead
 * - Issue rate
 */
const ProductMixPanel = ({ 
  vertical, 
  period = "mtd", 
  customStart, 
  customEnd,
  token,
  className = "" 
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductMix = async () => {
      if (!token) return;
      
      // Only fetch for specific verticals, not "all"
      if (!vertical || vertical === "all") {
        setData(null);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        let url = `${API_URL}/api/analytics/verticals/product-mix?vertical=${vertical}&period=${period}`;
        if (period === "custom" && customStart && customEnd) {
          url += `&custom_start=${customStart}&custom_end=${customEnd}`;
        }
        
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error("Failed to fetch product mix");
        
        const result = await res.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductMix();
  }, [vertical, period, customStart, customEnd, token]);

  // Don't render if no vertical selected or "all" verticals
  if (!vertical || vertical === "all") {
    return null;
  }

  if (loading) {
    return (
      <Card className={className} data-testid="product-mix-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Product Mix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`border-destructive/50 ${className}`} data-testid="product-mix-panel">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Failed to load product mix</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.product_mix?.length) {
    return (
      <Card className={className} data-testid="product-mix-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Product Mix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No product data available for this vertical.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "—";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined) return "—";
    return `${(val * 100).toFixed(1)}%`;
  };

  const verticalLabel = vertical === "mortgage_protection" ? "Mortgage Protection" 
    : vertical.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Card className={className} data-testid="product-mix-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Product Mix — {verticalLabel}
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs">
                  {data.total_leads} leads
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs space-y-1">
                  <p>Booked: {data.total_booked}</p>
                  <p>Held: {data.total_held}</p>
                  <p>Submitted: {data.total_submitted}</p>
                  <p>Issued: {data.total_issued}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Total Commission: {formatCurrency(data.total_commission)} | 
          Total Premium: {formatCurrency(data.total_premium)}
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-2 font-medium">Product Type</th>
                <th className="text-right py-2 font-medium">Issued</th>
                <th className="text-right py-2 font-medium">Commission</th>
                <th className="text-right py-2 font-medium">Rev/Lead</th>
                <th className="text-right py-2 font-medium">Issue Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.product_mix.map((pm, idx) => {
                const isLowConfidence = pm.sample_size < 10;
                return (
                  <tr 
                    key={pm.product_type} 
                    className={`border-b last:border-0 ${idx % 2 === 0 ? 'bg-muted/30' : ''}`}
                  >
                    <td className="py-2 font-medium">
                      <div className="flex items-center gap-2">
                        {pm.product_type}
                        {isLowConfidence && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertCircle className="h-3 w-3 text-yellow-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Low sample size ({pm.sample_size})</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className="py-2 text-right tabular-nums">{pm.issued_count}</td>
                    <td className="py-2 text-right tabular-nums font-medium text-green-600">
                      {formatCurrency(pm.commission_sum)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatCurrency(pm.revenue_per_lead)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <span className={pm.issue_rate >= 0.5 ? "text-green-600" : pm.issue_rate >= 0.3 ? "text-yellow-600" : "text-red-600"}>
                        {formatPercent(pm.issue_rate)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductMixPanel;
